import {VisuallyHidden} from '@reach/visually-hidden'
import {graphql, PageProps} from 'gatsby'
import React from 'react'

import FeatureBlock from '@/components/feature-block'
import Footer from '@/components/footer'
import Header from '@/components/header'
import SEO from '@/components/seo'
import {PricingPageQuery} from '@/graphql-types'
import PricingSection from '@/sections/pricing'
import QuoteCarousel from '@/sections/quote-carousel'
import SignupBannerSection from '@/sections/signup-banner'
import TitleWithAccordion from '@/sections/title-with-accordion'
import TitleWithIconGridSection from '@/sections/title-with-icon-grid'
import handleNoPrismicData from '@/utils/handle-no-prismic-data'
import {withPrismicPreview} from '@/utils/with-prismic-preview'
import RichText, {hasRichText} from '@helios/shared/components/rich-text'
import {Main} from '@helios/shared/components/skip-to-content'
import {Section, BackgroundWrapper, Layout} from '@helios/shared/styles/grid'
import {Text} from '@helios/shared/styles/typography'

import * as s from './pricing.styles'

type Props = PageProps<PricingPageQuery>

const dataTestId = 'pricing-page'

const renderCell = (text: string, isCheckmark: boolean, visuallyHiddenContent: string) => {
    if (text) {
        return <Text variant="label-base">{text}</Text>
    }

    if (isCheckmark) {
        return (
            <>
                <VisuallyHidden>{visuallyHiddenContent}</VisuallyHidden>
                <s.StyledTick />
            </>
        )
    }

    return null
}

const PricingPage = (props: Props) => {
    const data = props.data.prismicPricingPage.data

    if (!data) {
        return handleNoPrismicData()
    }

    return (
        <>
            <SEO
                title={data.meta_title}
                description={data.meta_description}
                openGraphImageUrl={data.open_graph_image?.url}
            />

            <Header />

            <Main>
                {hasRichText(data.title.raw) && (
                    <s.TitleSection data-testid={`${dataTestId}-title-section`}>
                        <s.TitleWrapper>
                            <RichText as="h1" content={data.title.raw} size="xl" />
                            {data.title_image && (
                                <s.StyledImage
                                    image={data.title_image}
                                    data-testid="pricing-hero-image"
                                />
                            )}
                        </s.TitleWrapper>
                    </s.TitleSection>
                )}

                <PricingSection dataTestId={`${dataTestId}-cards`} />

                <BackgroundWrapper color="muted">
                    {!!data.tables.filter(({table}) => !!table).length && (
                        <Section data-testId={`${dataTestId}-table`}>
                            <Layout as={s.TableTitleWrapper} variant="two-asymmetrical">
                                <Text size="lg">{data.tables_title}</Text>
                            </Layout>

                            {data.tables.map(({table}) => {
                                if (!table.document) {
                                    return null
                                }

                                return (
                                    <s.TableWrapper key={table.document.id}>
                                        <s.Table>
                                            <tr>
                                                <th scope="col">{table.document.data.category}</th>
                                                <th scope="col">
                                                    {table.document.data.essential_heading}
                                                </th>
                                                <th scope="col">
                                                    {table.document.data.pro_heading}
                                                </th>
                                                <th scope="col">
                                                    {table.document.data.premium_heading}
                                                </th>
                                            </tr>
                                            {table.document.data.rows.map((row, i) => (
                                                <tr key={`${row.feature}-${i}`}>
                                                    <th scope="row">{row.feature}</th>

                                                    <td>
                                                        {renderCell(
                                                            row.essential_text,
                                                            row.essential,
                                                            data.tables_tick_screen_reader_alt
                                                        )}
                                                    </td>

                                                    <td>
                                                        {renderCell(
                                                            row.pro_text,
                                                            row.pro,
                                                            data.tables_tick_screen_reader_alt
                                                        )}
                                                    </td>

                                                    <td>
                                                        {renderCell(
                                                            row.premium_text,
                                                            row.premium,
                                                            data.tables_tick_screen_reader_alt
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </s.Table>
                                    </s.TableWrapper>
                                )
                            })}
                        </Section>
                    )}
                </BackgroundWrapper>

                {!!data.questions_answers.length && (
                    <TitleWithAccordion
                        dataTestId={dataTestId}
                        title={data.questions_title}
                        items={data.questions_answers?.map((item) => ({
                            title: item.question,
                            body: item.answer?.raw,
                        }))}
                    />
                )}

                {data.testimonial.document && (
                    <QuoteCarousel
                        dataTestId={`${dataTestId}-quotes`}
                        items={[
                            {
                                image: data.testimonial.document.data.image,
                                quote: data.testimonial.document.data.quote,
                                mirrored: true,
                                attribution: {
                                    name: data.testimonial.document.data.attribution_name,
                                    title: data.testimonial.document.data.attribution_title,
                                },
                            },
                        ]}
                    />
                )}

                {data.social_proof_ratings_section.document && (
                    <TitleWithIconGridSection
                        dataTestId={`${dataTestId}-ratings`}
                        large
                        highlightColor="pink"
                        title={data.social_proof_ratings_section.document.data.title.raw}
                        items={data.social_proof_ratings_section.document.data.ratings.map(
                            ({rating}) => ({
                                icon: rating.document?.data.logo,
                                stars: rating.document?.data.stars,
                            })
                        )}
                    />
                )}

                {data.social_proof_logos_extended_section.document && (
                    <TitleWithIconGridSection
                        dataTestId={`${dataTestId}-logos`}
                        large
                        highlightColor="yellow"
                        title={data.social_proof_logos_extended_section.document.data.title.raw}
                        items={data.social_proof_logos_extended_section.document.data.logos.map(
                            ({logo}) => ({
                                icon: logo,
                            })
                        )}
                    />
                )}

                {data.why_pleo.map((block, i) => (
                    <Section key={`${block.title}-${i}`} data-testid={`${dataTestId}-why-pleo`}>
                        <FeatureBlock
                            {...{...block, body: block.body.raw}}
                            preTitle={block.pre_title}
                        />
                    </Section>
                ))}

                {data.signup_banner.document && (
                    <SignupBannerSection
                        dataTestId={`${dataTestId}-signup-banner`}
                        {...data.signup_banner.document.data}
                        color="pink"
                        placeholder={data.signup_banner.document.data.email_input_placeholder}
                        submitText={data.signup_banner.document.data.submit_text}
                        errorText={data.signup_banner.document.data.email_input_error_text}
                    />
                )}
            </Main>

            <Footer />
        </>
    )
}

export const query = graphql`
    query PricingPage($locale: String!) {
        prismicPricingPage(lang: {eq: $locale}) {
            _previewable
            data {
                meta_title
                meta_description
                open_graph_image {
                    alt
                    copyright
                    url
                    dimensions {
                        height
                        width
                    }
                }
                title_image {
                    alt
                    copyright
                    url
                    dimensions {
                        height
                        width
                    }
                }
                title {
                    raw
                }
                title_image {
                    alt
                    copyright
                    url
                    dimensions {
                        height
                        width
                    }
                }
                tables_title
                tables_tick_screen_reader_alt
                tables {
                    table {
                        document {
                            ... on PrismicPricingTable {
                                id
                                data {
                                    category
                                    essential_heading
                                    pro_heading
                                    premium_heading
                                    rows {
                                        feature
                                        essential_text
                                        essential
                                        pro_text
                                        pro
                                        premium_text
                                        premium
                                    }
                                }
                            }
                        }
                    }
                }
                questions_title
                questions_answers {
                    question
                    answer {
                        raw
                    }
                }
                testimonial {
                    document {
                        ... on PrismicTestimonial {
                            id
                            data {
                                image {
                                    alt
                                    copyright
                                    url
                                    dimensions {
                                        height
                                        width
                                    }
                                }
                                quote
                                attribution_name
                                attribution_title
                                cta_text
                            }
                        }
                    }
                }
                social_proof_logos_extended_section {
                    document {
                        ... on PrismicSocialProofLogosExtendedSection {
                            data {
                                title {
                                    raw
                                }
                                logos {
                                    logo {
                                        alt
                                        copyright
                                        url
                                        dimensions {
                                            height
                                            width
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
                social_proof_ratings_section {
                    document {
                        ... on PrismicSocialProofRatingsSection {
                            data {
                                title {
                                    raw
                                }
                                ratings {
                                    rating {
                                        document {
                                            ... on PrismicRating {
                                                data {
                                                    logo {
                                                        alt
                                                        copyright
                                                        url
                                                        dimensions {
                                                            height
                                                            width
                                                        }
                                                    }
                                                    stars
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
                why_pleo {
                    pre_title
                    image {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    title
                    body {
                        raw
                    }
                }
                signup_banner {
                    document {
                        ...SignupBanner
                    }
                }
            }
        }
    }
`

export default withPrismicPreview(PricingPage)
